import { PnConsultationAnswers } from '@bariendo/lib/pn-consultation-note';
import {
  describeEmployerBenefit,
  PN_EMPLOYMENT_ID,
  PN_INSURANCE_ELIGIBILITY_ID,
  PN_INSURANCE_SOURCE_ID,
  PN_INSURER_ID,
} from '@bariendo/lib/pn-consultation-script';
import dayjs from 'dayjs';

function text(answers: PnConsultationAnswers, id: string): string {
  const value = answers.values[id];
  return typeof value === 'string' ? value : '';
}

/**
 * The one-line value a beat shows when collapsed. An empty string means the beat
 * is untouched, which is also what drives its tick.
 *
 * With no checkboxes on this card, a beat ticks off what it *captured* — never
 * off "the navigator says they read it".
 */
export function summarizePnConsultationBeat(beatId: string, answers: PnConsultationAnswers): string {
  switch (beatId) {
    case 'rapport':
      return text(answers, 'source');

    case 'motivation':
      // `prior` arrives from the eligibility form before the beat is opened, so
      // it cannot be the tick — that would claim a beat nobody has worked.
      return text(answers, 'mo_glp');

    case 'services':
      return text(answers, 'procedure');

    case 'qualify': {
      // Smoking is always asked; pregnancy only when the gate is open. Either
      // answer means the navigator has been through the beat.
      const smoking = text(answers, 'ql_smoking');
      const pregnancy = text(answers, 'ql_preg');
      return smoking || pregnancy ? 'Confirmed' : '';
    }

    // The result once there is one; until then, that employment was asked.
    case 'employer':
      return describeEmployerBenefit(answers)?.label ?? text(answers, PN_EMPLOYMENT_ID);

    // The likely-eligibility line once there is one, else the insurer or source.
    case 'insurance':
      return (
        text(answers, PN_INSURANCE_ELIGIBILITY_ID) ||
        text(answers, PN_INSURER_ID) ||
        text(answers, PN_INSURANCE_SOURCE_ID)
      );

    case 'next': {
      const payment = text(answers, 'pay');
      if (payment) return payment;

      if (answers.procedureDate) {
        const day = dayjs(answers.procedureDate.start);
        return day.isValid() ? day.format('ddd, MMM D') : answers.procedureDate.start;
      }
      return text(answers, 'procdate') || text(answers, 'loc');
    }

    case 'notes':
      return text(answers, 'close');

    default:
      return '';
  }
}
